/*
 * Copyright (c) 2024. Devtron Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { AppConfigProps, URLS as COMMON_URLS, EnvResourceType, InfrastructureManagementAppListType } from '@devtron-labs/devtron-fe-common-lib'
import { generatePath } from 'react-router-dom'

export const CREATE_CLUSTER_PATH = 'create/cluster/:type(connect-using-server-url|connect-using-kubeconfig|create-cluster|add-isolated-cluster)'

export const URLS = {
    APPLICATION_MANAGEMENT_APP: COMMON_URLS.APPLICATION_MANAGEMENT_APP,
    APPLICATION_MANAGEMENT_APPLICATION_GROUP: COMMON_URLS.APPLICATION_MANAGEMENT_APPLICATION_GROUP,
    APPLICATION_MANAGEMENT_BULK_EDIT: `${COMMON_URLS.APPLICATION_MANAGEMENT}/bulk-edit`,
    APPLICATION_MANAGEMENT_CONFIGURATIONS_GITOPS: `${COMMON_URLS.APPLICATION_MANAGEMENT_CONFIGURATIONS}/gitops`,
    APPLICATION_MANAGEMENT_CONFIGURATIONS_GIT_ACCOUNTS: `${COMMON_URLS.APPLICATION_MANAGEMENT_CONFIGURATIONS}/git-accounts`,
    APPLICATION_MANAGEMENT_CONFIGURATIONS_NOTIFICATIONS: COMMON_URLS.APPLICATION_MANAGEMENT_CONFIGURATIONS_NOTIFICATIONS,
    APPLICATION_MANAGEMENT_CONFIGURATIONS_NOTIFICATIONS_ADD_NEW: `${COMMON_URLS.APPLICATION_MANAGEMENT_CONFIGURATIONS}/notifications/new`,
    INFRASTRUCTURE_MANAGEMENT_CHART_STORE: COMMON_URLS.INFRASTRUCTURE_MANAGEMENT_CHART_STORE,
    INFRASTRUCTURE_MANAGEMENT_CHART_STORE_DISCOVER: COMMON_URLS.INFRASTRUCTURE_MANAGEMENT_CHART_STORE_DISCOVER,
    AUTOMATION_AND_ENABLEMENT_JOB: COMMON_URLS.AUTOMATION_AND_ENABLEMENT_JOB,
    CREATE_JOB: 'create-job',
    RESOURCE_BROWSER_INSTALLATION_CLUSTER: `${COMMON_URLS.INFRASTRUCTURE_MANAGEMENT_RESOURCE_BROWSER}/installation-cluster/:installationId`,
    RESOURCE_BROWSER_CREATE_CLUSTER: `${COMMON_URLS.INFRASTRUCTURE_MANAGEMENT_RESOURCE_BROWSER}/${CREATE_CLUSTER_PATH}`,
    EXTERNAL_APPS: COMMON_URLS.EXTERNAL_APPS,
    DEVTRON_CHARTS: 'dc',
    EXTERNAL_ARGO_APP: 'eaa',
    EXTERNAL_FLUX_APP: 'external-flux',
    APP_LIST: 'list',
    APP_VALUES: 'values',
    APP_DETAILS: 'details',
    APP_DEPLOYMNENT_HISTORY: 'deployments',
    APP_DETAILS_K8: 'k8s-resources', // for V2
    APP_DETAILS_LOG: 'log-analyzer', // for V2
    APP_DETAILS_DEFAULT: 'default-view',
    APP_DIFF_VIEW: 'diff-view',
    APP_TRIGGER: 'trigger',
    APP_OVERVIEW: 'overview',
    MANAGE_TRAFFIC: 'manage-traffic',
    APP_CI_DETAILS: 'ci-details',
    APP_CD_DETAILS: 'cd-details',
    APP_DEPLOYMENT_METRICS: 'deployment-metrics',
    APP_GIT_CONFIG: 'materials',
    APP_DOCKER_CONFIG: 'docker-build-config',
    APP_GITOPS_CONFIG: 'gitops-config',
    APP_DOCKER_OVERRIDE_DETAILS: 'override-details',
    APP_DEPLOYMENT_CONFIG: EnvResourceType.DeploymentTemplate,
    APP_WORKFLOW_CONFIG: 'workflow',
    APP_CM_CONFIG: EnvResourceType.ConfigMap,
    APP_CS_CONFIG: EnvResourceType.Secret,
    APP_ENV_OVERRIDE_CONFIG: 'env-override',
    APP_ENV_CONFIG_COMPARE: 'config-compare',
    APP_EXTERNAL_LINKS: 'external-links',
    APP_CI_CONFIG: 'ci-pipeline',
    APP_CD_CONFIG: 'cd-pipeline',
    APP_CI_CD_CONFIG: 'ci-cd-pipeline',
    APP_EXTERNAL_CI_CONFIG: 'external-ci',
    APP_LINKED_CI_CONFIG: 'linked-ci',
    APP_JOB_CI_CONFIG: 'ci-job',
    AUTHENTICATE: '/auth/login',
    BASE_CONFIG: 'base-config',
    LINKED_CD: 'linked-cd',
    LOGIN: '/login',
    LOGIN_ADMIN: '/login/admin', //
    LOGIN_SSO: '/login/sso',
    GLOBAL_CONFIG_HOST_URL: `${COMMON_URLS.GLOBAL_CONFIG}/host-url`,
    GLOBAL_CONFIG_EXTERNAL_LINKS: `${COMMON_URLS.GLOBAL_CONFIG}/external-links`,
    GLOBAL_CONFIG_CHART_REPO: `${COMMON_URLS.GLOBAL_CONFIG}/chart-repositories`,
    GLOBAL_CONFIG_DOCKER: `${COMMON_URLS.GLOBAL_CONFIG}/docker`,
    GLOBAL_CONFIG_CLUSTER: `${COMMON_URLS.GLOBAL_CONFIG}/cluster-env`,
    GLOBAL_CONFIG_CREATE_CLUSTER: `${COMMON_URLS.GLOBAL_CONFIG}/cluster-env/${CREATE_CLUSTER_PATH}`,
    GLOBAL_CONFIG_AUTH: `${COMMON_URLS.GLOBAL_CONFIG}/auth`,
    GLOBAL_CONFIG_AUTH_USER_PERMISSION: `${COMMON_URLS.GLOBAL_CONFIG}/auth/users`,
    GLOBAL_CONFIG_AUTH_PERMISSION_GROUPS: `${COMMON_URLS.GLOBAL_CONFIG}/auth/groups`,
    GUIDE: 'guide',
    GETTING_STARTED: 'getting-started',
    LINKED_CI_DETAILS: 'linked-ci-details',
    STACK_MANAGER: '/stack-manager',
    STACK_MANAGER_DISCOVER_MODULES: '/stack-manager/discover',
    STACK_MANAGER_DISCOVER_MODULES_DETAILS: '/stack-manager/discover/details',
    STACK_MANAGER_INSTALLED_MODULES: '/stack-manager/installed',
    STACK_MANAGER_INSTALLED_MODULES_DETAILS: '/stack-manager/installed/details',
    STACK_MANAGER_ABOUT: '/stack-manager/about',
    STACK_MANAGER_ABOUT_RELEASES: '/stack-manager/about/releases',
    DEPLOYMENT_HISTORY_CONFIGURATIONS: '/configuration',
    NODES_LIST: '/nodes',
    NODE_DETAILS: '/node-details',
    CHART: '/chart',
    PRESET_VALUES: '/preset-values',
    DEPLOY_CHART: '/deploy-chart',
    DETAILS: '/details',
    DEVTRON_APP_LIST: COMMON_URLS.APPLICATION_MANAGEMENT_APP_LIST,
    HELM_APP_LIST: generatePath(COMMON_URLS.INFRASTRUCTURE_MANAGEMENT_APP_LIST, { appType: InfrastructureManagementAppListType.HELM }),
    ARGO_APP_LIST: generatePath(COMMON_URLS.INFRASTRUCTURE_MANAGEMENT_APP_LIST, { appType: InfrastructureManagementAppListType.ARGO_CD }),
    FLUX_APP_LIST: generatePath(COMMON_URLS.INFRASTRUCTURE_MANAGEMENT_APP_LIST, { appType: InfrastructureManagementAppListType.FLUX_CD }),
    BUILD: '/build',
    WEBHOOK_MODAL: 'webhook',
    MONITORING_DASHBOARD: 'monitoring-dashboard',
    CREATE_ENVIRONMENT: '/create/environment',
    POD_SPREAD: 'pod-spread',
    HIBERNATION_RULES: 'hibernation-rules',
    DELETE_CLUSTER: 'delete-cluster',
} as const

export enum APP_COMPOSE_STAGE {
    SOURCE_CONFIG = 'MATERIAL',
    CI_CONFIG = 'CI_CONFIG',
    DEPLOYMENT_TEMPLATE = 'DEPLOYMENT_TEMPLATE',
    WORKFLOW_EDITOR = 'WORKFLOW_EDITOR',
    CONFIG_MAPS = 'CONFIG_MAPS',
    SECRETS = 'SECRETS',
    ENV_OVERRIDE = 'ENV_OVERRIDE',
}

const ORDERED_APP_COMPOSE_ROUTES: { stage: string; path: string }[] = [
    { stage: APP_COMPOSE_STAGE.SOURCE_CONFIG, path: URLS.APP_GIT_CONFIG },
    { stage: APP_COMPOSE_STAGE.CI_CONFIG, path: URLS.APP_DOCKER_CONFIG },
    { stage: APP_COMPOSE_STAGE.DEPLOYMENT_TEMPLATE, path: `${URLS.BASE_CONFIG}/${URLS.APP_DEPLOYMENT_CONFIG}` },
    { stage: APP_COMPOSE_STAGE.WORKFLOW_EDITOR, path: URLS.APP_WORKFLOW_CONFIG },
    { stage: APP_COMPOSE_STAGE.CONFIG_MAPS, path: `${URLS.BASE_CONFIG}/${URLS.APP_CM_CONFIG}` },
    { stage: APP_COMPOSE_STAGE.SECRETS, path: `${URLS.BASE_CONFIG}/${URLS.APP_CS_CONFIG}` },
    { stage: APP_COMPOSE_STAGE.ENV_OVERRIDE, path: URLS.APP_ENV_OVERRIDE_CONFIG },
]

export const getAppComposeURL = (
    appId: string,
    appStage: APP_COMPOSE_STAGE | null,
    isJobView: boolean | null,
    isTemplateView: AppConfigProps['isTemplateView'],
): string => {
    const _url = isTemplateView
        ? `${generatePath(COMMON_URLS.APPLICATION_MANAGEMENT_TEMPLATES_DEVTRON_APP_DETAIL, {
              appId,
          })}/${COMMON_URLS.APP_CONFIG}`
        : `${isJobView ? URLS.AUTOMATION_AND_ENABLEMENT_JOB : URLS.APPLICATION_MANAGEMENT_APP}/${appId}/${COMMON_URLS.APP_CONFIG}`
    if (!appStage) {
        return _url
    }
    for (const stageDetail of ORDERED_APP_COMPOSE_ROUTES) {
        const { stage, path } = stageDetail
        if (stage === appStage) {
            return `${_url}/${path}`
        }
    }
    return `${_url}/${URLS.APP_GIT_CONFIG}`
}
